/**
 * AddInformantFlow
 * ================
 *
 * Two-step flow for adding a brand-new informant to an uncovered life stage.
 *
 * Step 0 — Enter the new informant's details (name, email, relationship)
 * Step 1 — Review & confirm (shows a summary card)
 * Done   — Success confirmation screen
 *
 * The life stage is predetermined by the parent (passed as the `lifeStage`
 * prop), since the user already picked "Add Adulthood/Childhood Informant"
 * from the summary screen.
 *
 * All form state is owned internally so the parent doesn't need to track
 * inputs, validation errors, or the form-vs-confirm sub-screen.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import StepIndicator from './StepIndicator';
import WarningPanel from './WarningPanel';
import ConfirmationScreen from './ConfirmationScreen';
import ButtonSpinner from './ButtonSpinner';
import InformantFormInput from './InformantFormInput';
import {
  validateNewInformant,
  validateEmailNotDuplicate,
  findReusableInformantForLifeStage,
} from '../../utils/informantValidation';

/**
 * @param {Object}   props
 * @param {string}   props.lifeStage           – 'Adulthood' or 'Childhood'
 * @param {Array}    props.allInformants        – All current informants (duplicate email check)
 * @param {Function} props.onSetup              – (details) => Promise – calls setupNewInformant
 * @param {Function} [props.onReuseExisting]    – (informantId, lifeStage) => Promise – calls
 *                                                reassignQuestionnaires when the entered email
 *                                                matches an existing informant for the other
 *                                                life stage (Adult ADHD same-person reuse).
 * @param {Function} props.onBack               – Navigate back to the summary screen
 * @param {boolean}  props.actionLoading        – Whether the backend call is in progress
 * @param {string}   props.actionError          – Error message from the backend
 * @param {Function} props.clearActionState     – Reset actionError/actionSuccess
 * @param {Function} props.onFinish             – Called when user clicks "Finish" on success screen
 * @param {boolean}  props.isFinishing          – Show spinner on the "Finish" button
 * @param {Object}   props.terminology          – { singular, lowerSingular }
 * @param {Function} props.sanitizeNameInput    – Name input sanitizer
 * @param {Function} props.sanitizeEmailInput   – Email input sanitizer
 * @param {string}   props.organisation         – Patient's organisation (for backend payload)
 * @param {boolean}  [props.isAutismPathway]    – Adult Autism: no life-stage, omit informantForLifeStage
 * @param {string}   [props.cypRole]            – CYP ADHD only: 'legalGuardian' | 'schoolRep'.
 *                                                Role-based (not life-staged); schoolRep adds a
 *                                                required school name field. Never shown as a
 *                                                generic "Informant" — the UI says Parent/carer
 *                                                or School representative.
 */

/** Role-specific copy for the CYP ADHD pathway. */
const CYP_ROLE_COPY = {
  legalGuardian: {
    title: 'Add parent/carer',
    noun: 'parent/carer',
    successHeadline: 'Parent/carer added successfully!',
    description: "This person is the child's responsible parent or carer. They will answer questionnaires about the child's history and behaviour.",
    relationshipPlaceholder: 'e.g. Parent, Foster carer',
    assignedTo: 'Parent/carer questionnaires',
  },
  schoolRep: {
    title: 'Add school representative',
    noun: 'school representative',
    successHeadline: 'School representative added successfully!',
    description: "This person is from the child's school or college - like their SENCO or class teacher. They will answer a few questionnaires about how the child is getting on at school.",
    relationshipPlaceholder: 'e.g. Class teacher, SENCO',
    assignedTo: 'School questionnaires',
  },
};

export default function AddInformantFlow({
  lifeStage,
  allInformants = [],
  onSetup,
  onReuseExisting,
  onBack,
  actionLoading = false,
  actionError = null,
  clearActionState,
  onFinish,
  isFinishing = false,
  terminology = { singular: 'Informant', lowerSingular: 'informant' },
  sanitizeNameInput,
  sanitizeEmailInput,
  organisation = '',
  isAutismPathway = false,
  callerType = 'provider',
  cypRole = null,
}) {
  const cypCopy = cypRole ? CYP_ROLE_COPY[cypRole] : null;
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    relationship: '',
    schoolName: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);

  // Same-person reuse detection (Adult ADHD only).
  // When the email entered matches an existing informant who covers the
  // OTHER life stage but not this one, we offer to reuse that person via
  // reassignQuestionnaires instead of creating a new account.
  const [reuseMatch, setReuseMatch] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const handleFormChange = useCallback((field, value) => {
    let sanitized = value;
    if (field === 'firstName' || field === 'lastName' || field === 'relationship' || field === 'schoolName') {
      sanitized = sanitizeNameInput?.(value) ?? value;
    } else if (field === 'email') {
      sanitized = sanitizeEmailInput?.(value) ?? value;
    }
    setFormData(prev => ({ ...prev, [field]: sanitized }));
    if (formErrors[field]) {
      setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    }
  }, [formErrors, sanitizeNameInput, sanitizeEmailInput]);

  // Same-person reuse is only available on the Adult ADHD pathway (life-stage
  // based) and only when the caller provides the onReuseExisting handler.
  // Autism and CYP pathways have different informant models that don't support
  // same-person reuse across slots.
  const canReuse = !isAutismPathway && !cypRole && !!lifeStage && !!onReuseExisting;

  const handleProceedToConfirm = useCallback(() => {
    // Life stages only apply to the adult ADHD pathway — autism and CYP are
    // role/slot based.
    const usesLifeStage = !isAutismPathway && !cypRole;
    const dataWithStage = usesLifeStage ? { ...formData, lifeStage } : formData;

    // Check for same-person reuse before running full validation.
    // If found, we skip name/relationship validation because those fields
    // are irrelevant — the existing person's details will be used.
    const reusable = canReuse
      ? findReusableInformantForLifeStage(formData.email, allInformants, lifeStage)
      : null;

    if (reusable) {
      // Only validate the email field (format + that it's still reusable, not
      // same-stage duplicate). Skip name/relationship — we'll use the existing
      // person's details on the confirm screen.
      const emailFormatErr = validateNewInformant(
        { ...dataWithStage, firstName: 'x', lastName: 'x', relationship: 'x' },
        allInformants,
        { requireLifeStage: usesLifeStage, reuseForLifeStage: lifeStage },
      );
      if (emailFormatErr.email) {
        setFormErrors({ email: emailFormatErr.email });
        return;
      }
      setReuseMatch(reusable);
      setFormErrors({});
      clearActionState?.();
      setStep(1);
      return;
    }

    // Standard path: no reuse — validate all fields normally.
    setReuseMatch(null);
    const errors = validateNewInformant(
      dataWithStage,
      allInformants,
      { requireLifeStage: usesLifeStage },
    );
    if (!errors.email) {
      const dupErr = validateEmailNotDuplicate(formData.email, allInformants);
      if (dupErr) errors.email = dupErr;
    }
    if (cypRole === 'schoolRep' && !formData.schoolName.trim()) {
      errors.schoolName = "Enter the name of the child's school or college";
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    clearActionState?.();
    setStep(1);
  }, [formData, lifeStage, allInformants, clearActionState, isAutismPathway, cypRole, canReuse]);

  const handleConfirm = useCallback(async () => {
    try {
      // Same-person reuse path: call reassignQuestionnaires instead of
      // creating a new account. The existing informant already has Auth
      // credentials and a profile doc — we just add the new life stage.
      if (reuseMatch) {
        await onReuseExisting(reuseMatch.id, lifeStage);
        if (mountedRef.current) setIsSuccess(true);
        return;
      }

      // Standard new-informant path.
      const payload = {
        informantFirstName: formData.firstName.trim(),
        informantLastName: formData.lastName.trim(),
        informantEmail: formData.email.trim().toLowerCase(),
        informantRelationshipToPatient: formData.relationship.trim(),
        organisation,
      };
      if (cypRole) {
        // CYP ADHD: role-based; the hook stamps assessmentModule cypAdhd.
        payload.informantRole = cypRole;
        if (cypRole === 'schoolRep') {
          payload.schoolName = formData.schoolName.trim();
        }
      } else if (!isAutismPathway) {
        payload.informantForLifeStage = lifeStage;
      }
      await onSetup(payload);
      if (mountedRef.current) setIsSuccess(true);
    } catch {
      // actionError handled by the hook
    }
  }, [onSetup, onReuseExisting, reuseMatch, formData, lifeStage, organisation, isAutismPathway, cypRole]);

  // ── Success screen ──
  if (isSuccess) {
    // Reuse path: the person already has an account; they just gained a new
    // questionnaire assignment. Don't claim a welcome email was sent.
    if (reuseMatch) {
      return (
        <ConfirmationScreen
          headline="Questionnaire assigned successfully!"
          detail={`${reuseMatch.firstName} ${reuseMatch.lastName} is now assigned to the ${lifeStage?.toLowerCase()} questionnaire.`}
          onFinish={onFinish}
          isLoading={isFinishing}
        />
      );
    }

    const successDetail = cypCopy
      ? `${formData.firstName.trim()} ${formData.lastName.trim()} has been set up as the ${cypCopy.noun}. A welcome email has been sent.`
      : isAutismPathway
        ? `${formData.firstName.trim()} ${formData.lastName.trim()} has been set up to answer developmental history questions. A welcome email has been sent.`
        : `${formData.firstName.trim()} ${formData.lastName.trim()} has been set up for the ${lifeStage?.toLowerCase()} questionnaire. A welcome email has been sent.`;
    return (
      <ConfirmationScreen
        headline={cypCopy ? cypCopy.successHeadline : `${terminology.singular} added successfully!`}
        detail={successDetail}
        onFinish={onFinish}
        isLoading={isFinishing}
      />
    );
  }

  // ── Step 1: Confirm ──
  if (step === 1) {
    // When reusing an existing person, show their profile details (not the
    // typed form data) and an info banner explaining the reuse.  The existing
    // person's name, email, and relationship are what the backend will use.
    const displayName = reuseMatch
      ? `${reuseMatch.firstName} ${reuseMatch.lastName}`
      : `${formData.firstName.trim()} ${formData.lastName.trim()}`;
    const displayEmail = reuseMatch
      ? reuseMatch.email
      : formData.email.trim();
    const displayRelationship = reuseMatch
      ? reuseMatch.relationship
      : formData.relationship.trim();
    const otherStageLabel = reuseMatch
      ? (reuseMatch.lifeStages || [])[0]?.toLowerCase() || 'other'
      : null;

    return (
      <div className="space-y-4">
        <StepIndicator steps={['Details', 'Confirm']} currentStep={1} />

        <div className="border-b border-gray-200 pb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            {reuseMatch
              ? `Assign ${lifeStage} questionnaire`
              : cypCopy ? `Confirm new ${cypCopy.noun}` : `Confirm New ${terminology.singular}`}
          </h3>
        </div>

        {/* Same-person reuse banner — only shown in the Adult ADHD reuse path */}
        {reuseMatch && (
          <WarningPanel variant="info">
            That email is already in use for your {otherStageLabel}{' '}
            {terminology.lowerSingular}. We will reuse their existing details
            and assign the {lifeStage?.toLowerCase()} questionnaire to them.
          </WarningPanel>
        )}

        <div className="bg-violet-50 border border-violet-200 rounded-md p-3">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <dt className="text-gray-500">Name</dt>
            <dd className="font-medium text-gray-800">
              {displayName}
            </dd>
            <dt className="text-gray-500">Email</dt>
            <dd className="font-medium text-gray-800 break-all">
              {displayEmail}
            </dd>
            <dt className="text-gray-500">Relationship</dt>
            <dd className="font-medium text-gray-800">
              {displayRelationship}
            </dd>
            {cypRole === 'schoolRep' && (
              <>
                <dt className="text-gray-500">School or college</dt>
                <dd className="font-medium text-gray-800">
                  {formData.schoolName.trim()}
                </dd>
              </>
            )}
            <dt className="text-gray-500">Assigned to</dt>
            <dd className="font-medium text-gray-800">
              {cypCopy
                ? cypCopy.assignedTo
                : isAutismPathway ? 'Developmental history questionnaires' : `${lifeStage} questionnaire`}
            </dd>
          </dl>
        </div>

        {/* Welcome email notice — only shown for new informants, not reuse */}
        {!reuseMatch && (
          <WarningPanel variant="info">
            A welcome email will be sent to{' '}
            <span className="font-semibold break-all">{formData.email.trim()}</span>.
          </WarningPanel>
        )}

        {actionError && <WarningPanel variant="error">{actionError}</WarningPanel>}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={() => { clearActionState?.(); setReuseMatch(null); setStep(0); }}
            disabled={actionLoading}
            className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={actionLoading}
            className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
              actionLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {actionLoading ? (
              <span className="inline-flex items-center gap-2">
                <ButtonSpinner />
                {reuseMatch ? 'Assigning…' : 'Setting up…'}
              </span>
            ) : reuseMatch ? 'Confirm Assignment' : `Set up ${terminology.singular}`}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 0: Details form ──
  return (
    <div className="space-y-4">
      <StepIndicator steps={['Details', 'Confirm']} currentStep={0} />

      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          {cypCopy
            ? cypCopy.title
            : isAutismPathway ? `Add ${terminology.singular}` : `Add ${lifeStage} ${terminology.singular}`}
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          {cypCopy
            ? cypCopy.description
            : isAutismPathway
              ? `This person will answer developmental history questionnaires about ${callerType === 'patient' ? 'you' : 'the patient'}.`
              : `This person will answer the ${lifeStage?.toLowerCase()} questionnaire.`}
        </p>
      </div>

      <div className="space-y-3">
        <InformantFormInput
          label="First name"
          value={formData.firstName}
          onChange={(e) => handleFormChange('firstName', e.target.value)}
          error={formErrors.firstName}
          required
          autoComplete="given-name"
        />
        <InformantFormInput
          label="Last name"
          value={formData.lastName}
          onChange={(e) => handleFormChange('lastName', e.target.value)}
          error={formErrors.lastName}
          required
          autoComplete="family-name"
        />
        <InformantFormInput
          label="Email address"
          type="email"
          value={formData.email}
          onChange={(e) => handleFormChange('email', e.target.value)}
          error={formErrors.email}
          required
          autoComplete="email"
        />
        <InformantFormInput
          label={cypRole
            ? 'Relationship to the child'
            : callerType === 'patient' ? 'Relationship to you' : 'Relationship to patient'}
          value={formData.relationship}
          onChange={(e) => handleFormChange('relationship', e.target.value)}
          error={formErrors.relationship}
          required
          placeholder={cypCopy?.relationshipPlaceholder || 'e.g. Parent, Spouse, Sibling'}
        />
        {cypRole === 'schoolRep' && (
          <InformantFormInput
            label="School or college name"
            value={formData.schoolName}
            onChange={(e) => handleFormChange('schoolName', e.target.value)}
            error={formErrors.schoolName}
            required
          />
        )}
      </div>

      {actionError && <WarningPanel variant="error">{actionError}</WarningPanel>}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={actionLoading}
          className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleProceedToConfirm}
          disabled={actionLoading}
          className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Review Details →
        </button>
      </div>
    </div>
  );
}
